/**
 * Sound Manager
 * Handles music and sound effects using Web Audio API
 * Character-specific audio effects for abilities
 */

export class SoundManager {
  constructor() {
    this.audioContext = null;
    this.masterGain = null;
    this.musicGain = null;
    this.sfxGain = null;
    this.currentMusic = null;
    this.masterVolume = 1;
    this.musicVolume = 0.5;
    this.sfxVolume = 0.7;
    this._preMuteMasterVolume = null;

    this.sounds = {};
    this.musicTracks = {};
    this.characterSounds = {};
  }

  async init() {
    // Create audio context
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

    // Master gain node (single knob for overall volume + mute)
    this.masterGain = this.audioContext.createGain();
    this.masterGain.gain.value = this.masterVolume;
    this.masterGain.connect(this.audioContext.destination);

    // Create gain nodes
    this.musicGain = this.audioContext.createGain();
    this.musicGain.gain.value = this.musicVolume;
    this.musicGain.connect(this.masterGain);

    this.sfxGain = this.audioContext.createGain();
    this.sfxGain.gain.value = this.sfxVolume;
    this.sfxGain.connect(this.masterGain);

    // Generate procedural sounds
    this.generateSounds();
    this.generateCharacterSounds();

    return Promise.resolve();
  }

  generateSounds() {
    // Generate laser sound
    this.sounds.laser = this.createLaserSound();

    // Generate explosion sound
    this.sounds.explosion = this.createExplosionSound();

    // Generate bomb sound
    this.sounds.bomb = this.createBombSound();

    // Generate powerup sound
    this.sounds.powerup = this.createPowerupSound();

    // Generate gameover sound
    this.sounds.gameover = this.createGameoverSound();

    // Generate music tracks
    this.musicTracks.main = this.createMainTheme();
    this.musicTracks.combat = this.createCombatMusic();
    this.musicTracks.boss = this.createBossMusic();
    this.musicTracks.victory = this.createVictoryMusic();
  }

  generateCharacterSounds() {
    // Honey Badger - Aggressive, fast, no brakes
    this.characterSounds.honeybadger = {
      boost: this.createHoneyBadgerBoost(),
      weapon: this.createHoneyBadgerWeapon(),
      damage: this.createHoneyBadgerDamage(),
    };

    // Octopus - Strategic, multi-layered, corporate
    this.characterSounds.octopus = {
      boost: this.createOctopusBoost(),
      weapon: this.createOctopusWeapon(),
      damage: this.createOctopusDamage(),
      drone: this.createOctopusDrone(),
    };

    // Elephant - Heavy, methodical, powerful
    this.characterSounds.elephant = {
      boost: this.createElephantBoost(),
      weapon: this.createElephantWeapon(),
      damage: this.createElephantDamage(),
      charge: this.createElephantCharge(),
    };

    // Mantis Shrimp - Stealthy, iridescent, philosophical
    this.characterSounds.mantisshrimp = {
      boost: this.createMantisShrimpBoost(),
      weapon: this.createMantisShrimpWeapon(),
      damage: this.createMantisShrimpDamage(),
      stealth: this.createMantisShrimpStealth(),
    };

    // Ant - Industrial, swarm, logistics
    this.characterSounds.ant = {
      boost: this.createAntBoost(),
      weapon: this.createAntWeapon(),
      damage: this.createAntDamage(),
      swarm: this.createAntSwarm(),
    };
  }

  // ============================================================================
  // CHARACTER-SPECIFIC SOUNDS
  // ============================================================================

  // HONEY BADGER - RAGEBURROW Mk IV
  // High-pitched, aggressive, uncontrolled
  createHoneyBadgerBoost() {
    return () => {
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      const filter = this.audioContext.createBiquadFilter();

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.sfxGain);

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(440, this.audioContext.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1760, this.audioContext.currentTime + 0.3);

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(5000, this.audioContext.currentTime);
      filter.frequency.linearRampToValueAtTime(1000, this.audioContext.currentTime + 0.3);

      gain.gain.setValueAtTime(0.4, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

      osc.start(this.audioContext.currentTime);
      osc.stop(this.audioContext.currentTime + 0.3);
    };
  }

  createHoneyBadgerWeapon() {
    return () => {
      // Rapid-fire, high-pitched laser
      const baseFreq = 880;
      for (let i = 0; i < 3; i++) {
        setTimeout(() => {
          const osc = this.audioContext.createOscillator();
          const gain = this.audioContext.createGain();

          osc.connect(gain);
          gain.connect(this.sfxGain);

          osc.type = 'square';
          osc.frequency.setValueAtTime(baseFreq + (i * 100), this.audioContext.currentTime);
          osc.frequency.exponentialRampToValueAtTime(baseFreq - 200, this.audioContext.currentTime + 0.08);

          gain.gain.setValueAtTime(0.3, this.audioContext.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.08);

          osc.start(this.audioContext.currentTime);
          osc.stop(this.audioContext.currentTime + 0.08);
        }, i * 30);
      }
    };
  }

  createHoneyBadgerDamage() {
    return () => {
      // Screeching metal
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      const filter = this.audioContext.createBiquadFilter();

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.sfxGain);

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(200, this.audioContext.currentTime);
      osc.frequency.linearRampToValueAtTime(100, this.audioContext.currentTime + 0.2);

      filter.type = 'highpass';
      filter.frequency.value = 1000;

      gain.gain.setValueAtTime(0.5, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

      osc.start(this.audioContext.currentTime);
      osc.stop(this.audioContext.currentTime + 0.3);
    };
  }

  // OCTOPUS - MULTIVEIL
  // Layered, harmonic, corporate efficiency
  createOctopusBoost() {
    return () => {
      // Multi-oscillator chord
      const freqs = [330, 392, 494]; // E major chord
      freqs.forEach((freq, i) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();

        osc.connect(gain);
        gain.connect(this.sfxGain);

        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, this.audioContext.currentTime + i * 0.05);

        gain.gain.setValueAtTime(0, this.audioContext.currentTime + i * 0.05);
        gain.gain.linearRampToValueAtTime(0.2, this.audioContext.currentTime + i * 0.05 + 0.1);
        gain.gain.linearRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);

        osc.start(this.audioContext.currentTime + i * 0.05);
        osc.stop(this.audioContext.currentTime + 0.5);
      });
    };
  }

  createOctopusWeapon() {
    return () => {
      // Precision targeting sound
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      const filter = this.audioContext.createBiquadFilter();

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.sfxGain);

      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, this.audioContext.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1320, this.audioContext.currentTime + 0.15);

      filter.type = 'bandpass';
      filter.frequency.value = 1000;
      filter.Q.value = 10;

      gain.gain.setValueAtTime(0.2, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);

      osc.start(this.audioContext.currentTime);
      osc.stop(this.audioContext.currentTime + 0.15);
    };
  }

  createOctopusDamage() {
    return () => {
      // Glass shattering harmonics
      const freqs = [880, 1100, 1320];
      freqs.forEach((freq, i) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();

        osc.connect(gain);
        gain.connect(this.sfxGain);

        osc.type = 'sine';
        osc.frequency.value = freq;

        gain.gain.setValueAtTime(0.1, this.audioContext.currentTime + i * 0.02);
        gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

        osc.start(this.audioContext.currentTime + i * 0.02);
        osc.stop(this.audioContext.currentTime + 0.3);
      });
    };
  }

  createOctopusDrone() {
    return () => {
      // Drone deployment - layered hum
      const freqs = [110, 220, 330];
      freqs.forEach((freq) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();

        osc.connect(gain);
        gain.connect(this.sfxGain);

        osc.type = 'triangle';
        osc.frequency.value = freq;

        gain.gain.setValueAtTime(0, this.audioContext.currentTime);
        gain.gain.linearRampToValueAtTime(0.15, this.audioContext.currentTime + 0.2);
        gain.gain.linearRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);

        osc.start(this.audioContext.currentTime);
        osc.stop(this.audioContext.currentTime + 0.5);
      });
    };
  }

  // ELEPHANT - GRAY MATTER
  // Heavy, low-frequency, thunderous
  createElephantBoost() {
    return () => {
      // Ground-shaking low rumble
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      const filter = this.audioContext.createBiquadFilter();

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.sfxGain);

      osc.type = 'sine';
      osc.frequency.setValueAtTime(55, this.audioContext.currentTime);
      osc.frequency.exponentialRampToValueAtTime(110, this.audioContext.currentTime + 0.4);

      filter.type = 'lowpass';
      filter.frequency.value = 200;

      gain.gain.setValueAtTime(0.5, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);

      osc.start(this.audioContext.currentTime);
      osc.stop(this.audioContext.currentTime + 0.5);
    };
  }

  createElephantWeapon() {
    return () => {
      // Heavy cannon
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      const filter = this.audioContext.createBiquadFilter();

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.sfxGain);

      osc.type = 'square';
      osc.frequency.setValueAtTime(150, this.audioContext.currentTime);
      osc.frequency.exponentialRampToValueAtTime(50, this.audioContext.currentTime + 0.3);

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(800, this.audioContext.currentTime);
      filter.frequency.exponentialRampToValueAtTime(200, this.audioContext.currentTime + 0.3);

      gain.gain.setValueAtTime(0.4, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

      osc.start(this.audioContext.currentTime);
      osc.stop(this.audioContext.currentTime + 0.3);
    };
  }

  createElephantDamage() {
    return () => {
      // Armor impact - low thud
      const bufferSize = this.audioContext.sampleRate * 0.3;
      const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
      const data = buffer.getChannelData(0);

      // Create low-frequency noise burst
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
      }

      const noise = this.audioContext.createBufferSource();
      noise.buffer = buffer;

      const filter = this.audioContext.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 150;

      const gain = this.audioContext.createGain();
      gain.gain.setValueAtTime(0.5, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

      noise.connect(filter);
      filter.connect(gain);
      gain.connect(this.sfxGain);

      noise.start(this.audioContext.currentTime);
    };
  }

  createElephantCharge() {
    return () => {
      // Charging sound - rising low frequency
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();

      osc.connect(gain);
      gain.connect(this.sfxGain);

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(50, this.audioContext.currentTime);
      osc.frequency.linearRampToValueAtTime(100, this.audioContext.currentTime + 2);

      gain.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gain.gain.linearRampToValueAtTime(0.5, this.audioContext.currentTime + 2);

      osc.start(this.audioContext.currentTime);
      osc.stop(this.audioContext.currentTime + 2);
    };
  }

  // MANTIS SHRIMP - CHROMA WAR
  // High-frequency, shifting, ethereal
  createMantisShrimpBoost() {
    return () => {
      // Phase shift sound - frequency modulation
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      const lfo = this.audioContext.createOscillator();
      const lfoGain = this.audioContext.createGain();

      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      osc.connect(gain);
      gain.connect(this.sfxGain);

      osc.type = 'sine';
      osc.frequency.value = 660;

      lfo.type = 'sine';
      lfo.frequency.value = 15;
      lfoGain.gain.value = 200;

      gain.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.4);

      lfo.start(this.audioContext.currentTime);
      osc.start(this.audioContext.currentTime);
      lfo.stop(this.audioContext.currentTime + 0.4);
      osc.stop(this.audioContext.currentTime + 0.4);
    };
  }

  createMantisShrimpWeapon() {
    return () => {
      // Iridescent strike - high frequency shimmer
      const freqs = [880, 1100, 1320, 1760];
      freqs.forEach((freq, i) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();

        osc.connect(gain);
        gain.connect(this.sfxGain);

        osc.type = 'sine';
        osc.frequency.value = freq;

        gain.gain.setValueAtTime(0.15, this.audioContext.currentTime + i * 0.01);
        gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.2);

        osc.start(this.audioContext.currentTime + i * 0.01);
        osc.stop(this.audioContext.currentTime + 0.2);
      });
    };
  }

  createMantisShrimpDamage() {
    return () => {
      // Phase collapse - descending glissando
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();

      osc.connect(gain);
      gain.connect(this.sfxGain);

      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, this.audioContext.currentTime);
      osc.frequency.exponentialRampToValueAtTime(110, this.audioContext.currentTime + 0.4);

      gain.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.4);

      osc.start(this.audioContext.currentTime);
      osc.stop(this.audioContext.currentTime + 0.4);
    };
  }

  createMantisShrimpStealth() {
    return () => {
      // Stealth activation - filtering sweep
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      const filter = this.audioContext.createBiquadFilter();

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.sfxGain);

      osc.type = 'triangle';
      osc.frequency.value = 440;

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(5000, this.audioContext.currentTime);
      filter.frequency.linearRampToValueAtTime(200, this.audioContext.currentTime + 0.5);

      gain.gain.setValueAtTime(0.2, this.audioContext.currentTime);
      gain.gain.linearRampToValueAtTime(0.05, this.audioContext.currentTime + 0.5);

      osc.start(this.audioContext.currentTime);
      osc.stop(this.audioContext.currentTime + 0.5);
    };
  }

  // ANT - FULFILLMENT QUEEN
  // Industrial, rhythmic, swarm-like
  createAntBoost() {
    return () => {
      // Industrial hydraulic
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      const filter = this.audioContext.createBiquadFilter();

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.sfxGain);

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(200, this.audioContext.currentTime);
      osc.frequency.exponentialRampToValueAtTime(400, this.audioContext.currentTime + 0.3);

      filter.type = 'lowpass';
      filter.frequency.value = 1000;

      gain.gain.setValueAtTime(0.4, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

      osc.start(this.audioContext.currentTime);
      osc.stop(this.audioContext.currentTime + 0.3);
    };
  }

  createAntWeapon() {
    return () => {
      // Machine gun burst
      const baseFreq = 600;
      for (let i = 0; i < 5; i++) {
        setTimeout(() => {
          const osc = this.audioContext.createOscillator();
          const gain = this.audioContext.createGain();

          osc.connect(gain);
          gain.connect(this.sfxGain);

          osc.type = 'square';
          osc.frequency.value = baseFreq + Math.random() * 100;

          gain.gain.setValueAtTime(0.15, this.audioContext.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.05);

          osc.start(this.audioContext.currentTime);
          osc.stop(this.audioContext.currentTime + 0.05);
        }, i * 40);
      }
    };
  }

  createAntDamage() {
    return () => {
      // Metal clank
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      const filter = this.audioContext.createBiquadFilter();

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.sfxGain);

      osc.type = 'square';
      osc.frequency.setValueAtTime(300, this.audioContext.currentTime);
      osc.frequency.exponentialRampToValueAtTime(100, this.audioContext.currentTime + 0.15);

      filter.type = 'bandpass';
      filter.frequency.value = 500;
      filter.Q.value = 5;

      gain.gain.setValueAtTime(0.4, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);

      osc.start(this.audioContext.currentTime);
      osc.stop(this.audioContext.currentTime + 0.15);
    };
  }

  createAntSwarm() {
    return () => {
      // Swarm deployment - buzzing layers
      const freqs = [220, 330, 440, 550];
      freqs.forEach((freq, i) => {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();

        osc.connect(gain);
        gain.connect(this.sfxGain);

        osc.type = 'sawtooth';
        osc.frequency.value = freq + (Math.random() * 20 - 10);

        gain.gain.setValueAtTime(0, this.audioContext.currentTime + i * 0.05);
        gain.gain.linearRampToValueAtTime(0.1, this.audioContext.currentTime + i * 0.05 + 0.1);
        gain.gain.linearRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);

        osc.start(this.audioContext.currentTime + i * 0.05);
        osc.stop(this.audioContext.currentTime + 0.5);
      });
    };
  }

  // ============================================================================
  // BASE SOUNDS
  // ============================================================================

  createLaserSound() {
    return () => {
      const oscillator = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();

      oscillator.connect(gain);
      gain.connect(this.sfxGain);

      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(880, this.audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(110, this.audioContext.currentTime + 0.1);

      gain.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.1);
    };
  }

  createExplosionSound() {
    return () => {
      const bufferSize = this.audioContext.sampleRate * 0.5;
      const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
      const data = buffer.getChannelData(0);

      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }

      const noise = this.audioContext.createBufferSource();
      noise.buffer = buffer;

      const filter = this.audioContext.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 1000;

      const gain = this.audioContext.createGain();
      gain.gain.setValueAtTime(0.5, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);

      noise.connect(filter);
      filter.connect(gain);
      gain.connect(this.sfxGain);

      noise.start(this.audioContext.currentTime);
    };
  }

  createBombSound() {
    return () => {
      const oscillator = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();

      oscillator.connect(gain);
      gain.connect(this.sfxGain);

      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(200, this.audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(50, this.audioContext.currentTime + 0.5);

      gain.gain.setValueAtTime(0.5, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.5);
    };
  }

  createPowerupSound() {
    return () => {
      const oscillator = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();

      oscillator.connect(gain);
      gain.connect(this.sfxGain);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(440, this.audioContext.currentTime);
      oscillator.frequency.linearRampToValueAtTime(880, this.audioContext.currentTime + 0.2);

      gain.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gain.gain.linearRampToValueAtTime(0.01, this.audioContext.currentTime + 0.2);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.2);
    };
  }

  createGameoverSound() {
    return () => {
      const oscillator = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();

      oscillator.connect(gain);
      gain.connect(this.sfxGain);

      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(440, this.audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(55, this.audioContext.currentTime + 1);

      gain.gain.setValueAtTime(0.5, this.audioContext.currentTime);
      gain.gain.linearRampToValueAtTime(0.01, this.audioContext.currentTime + 1);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 1);
    };
  }

  createMainTheme() {
    // Simple ambient pad
    return this.createAmbientTrack([261.63, 329.63, 392.00], 4);
  }

  createCombatMusic() {
    // More intense
    return this.createAmbientTrack([196.00, 246.94, 293.66], 2);
  }

  createBossMusic() {
    // Dramatic
    return this.createAmbientTrack([146.83, 196.00, 246.94], 1);
  }

  createVictoryMusic() {
    // Triumphant
    return this.createAmbientTrack([523.25, 659.25, 783.99], 2);
  }

  createAmbientTrack(frequencies, _duration) {
    const oscillators = frequencies.map(freq => {
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();

      osc.type = 'sine';
      osc.frequency.value = freq;

      gain.gain.value = 0.1;

      osc.connect(gain);
      gain.connect(this.musicGain);

      return { osc, gain };
    });

    return {
      play: () => {
        oscillators.forEach(({ osc }) => {
          osc.start();
        });
      },
      stop: () => {
        oscillators.forEach(({ osc, gain }) => {
          gain.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 1);
          osc.stop(this.audioContext.currentTime + 1);
        });
      },
    };
  }

  // ============================================================================
  // PUBLIC METHODS
  // ============================================================================

  playSFX(name) {
    if (this.sounds[name]) {
      this.sounds[name]();
    }
  }

  /**
   * Play character-specific sound effect
   * @param {string} character - Character type (honeybadger, octopus, elephant, mantisshrimp, ant)
   * @param {string} soundType - Sound type (boost, weapon, damage, drone, charge, stealth, swarm)
   */
  playCharacterSound(character, soundType) {
    if (this.characterSounds[character] && this.characterSounds[character][soundType]) {
      this.characterSounds[character][soundType]();
    } else {
      // Fallback to generic sounds
      const fallbackMap = {
        weapon: 'laser',
        damage: 'explosion',
        boost: 'powerup',
      };
      if (fallbackMap[soundType]) {
        this.playSFX(fallbackMap[soundType]);
      }
    }
  }

  playMusic(name) {
    this.stopMusic();

    if (this.musicTracks[name]) {
      this.currentMusic = this.musicTracks[name];
      this.currentMusic.play();
    }
  }

  stopMusic() {
    if (this.currentMusic) {
      this.currentMusic.stop();
      this.currentMusic = null;
    }
  }

  pauseMusic() {
    if (this.musicGain) {
      this.musicGain.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.5);
    }
  }

  resumeMusic() {
    if (this.musicGain) {
      this.musicGain.gain.linearRampToValueAtTime(this.musicVolume, this.audioContext.currentTime + 0.5);
    }
  }

  setMusicVolume(volume) {
    this.musicVolume = volume;
    if (this.musicGain) {
      this.musicGain.gain.value = volume;
    }
  }

  setSFXVolume(volume) {
    this.sfxVolume = volume;
    if (this.sfxGain) {
      this.sfxGain.gain.value = volume;
    }
  }

  setVolume(volume) {
    this.masterVolume = volume;
    if (this.masterGain) {
      this.masterGain.gain.value = volume;
    }
  }

  mute(isMuted = true) {
    if (!this.masterGain) return;

    if (isMuted) {
      if (this._preMuteMasterVolume == null) this._preMuteMasterVolume = this.masterGain.gain.value;
      this.masterGain.gain.value = 0;
      return;
    }

    const restore = this._preMuteMasterVolume == null ? this.masterVolume : this._preMuteMasterVolume;
    this._preMuteMasterVolume = null;
    this.masterGain.gain.value = restore;
  }

  dispose() {
    this.stopMusic();
    if (this.audioContext) {
      this.audioContext.close();
    }
  }
}
